<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'

import RewardForm from '@/components/rewards/RewardForm.vue'

import { initializeRewardForm } from '@/components/rewards/utils/rewardFormInitialization'
import { hydrateItemsFromExternalId } from '@/components/rewards/utils/offerRewardHydration'
import { ensureRewardUsage } from '@/components/rewards/utils/rewardHelpers'

import type { LoyaltyReward, RewardUsage } from '@/types/types'
import { useRewardForm } from './compmosable/useRewardForm'

const props = withDefaults(
  defineProps<{
    rewardId?: number
    isExternalFormModified?: boolean
    usage?: RewardUsage
    hasErrors?: boolean
    hideClaimLimitBlock?: boolean
    isAddMode?: boolean
    saveReward?: (reward: LoyaltyReward) => Promise<void>
  }>(),
  { saveReward: undefined, hideClaimLimitBlock: false, isAddMode: false }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:reward', reward: LoyaltyReward): void
  (e: 'update:rewardTypeChanged', reward: LoyaltyReward): void
  (e: 'update:nameChanged', name: string): void
  (e: 'saveError', error: unknown): void
}>()

const { t } = useI18n()

const {
  currentReward,
  hasRewardFormErrors,
  isLoading,
  loadRewardById,
  loadLoyaltyProgramRewardById,
  rewardVouchers,
  fetchRewardTypes,
  isRewardTypesReady,
  rewardTypesError,
  isLoadingPartners,
  loadPartnerParameters
} = useRewardForm()

const rewardTypeIdsMap = computed(() => {
  if (rewardVouchers.value.length === 0) return null
  return rewardVouchers.value.reduce<Record<string, number>>((acc, v) => {
    acc[v.slug] = v.id
    return acc
  }, {})
})

const defaultRewardType = computed(() => {
  return rewardVouchers.value?.find((v) => v.slug === 'free_product') ?? null
})

const formatReward = (reward: LoyaltyReward | null) => {
  if (!reward) return ''

  const { usages: _usages, ...rest } = reward
  return JSON.stringify(rest)
}

const hasChanges = computed(() => {
  return formatReward(currentReward.value) !== formatReward(initialReward.value) || props.isExternalFormModified
})

const initialReward = ref<LoyaltyReward | null>(null)

const changesLoading = ref(false)

const rewardFormRef = ref<InstanceType<typeof RewardForm> | null>(null)

const pageTitle = computed(() => currentReward.value?.name || t('pages.reward.placeholder.newReward'))

const defaultUrl = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/rewards/default_reward.jpg`)

const handleSave = async () => {
  if (!currentReward.value) return

  if (!currentReward.value.name.trim() || hasRewardFormErrors.value) {
    rewardFormRef.value?.focusFirstError()
    return
  }

  changesLoading.value = true

  if (currentReward.value.pictureUrl === initialReward.value?.pictureUrl) {
    currentReward.value.pictureUrl = null
  }

  try {
    if (props.saveReward) {
      await props.saveReward(currentReward.value)
    } else {
      emit('update:reward', currentReward.value)
    }
  } catch (err) {
    if (props.saveReward) {
      emit('saveError', err)
    }
  } finally {
    changesLoading.value = false
  }
}

const handleClose = () => {
  emit('close')
}

const initializeOfferFormIfNeeded = () => {
  if (props.usage !== 'offer' || !currentReward.value) return
  initializeRewardForm(currentReward.value, rewardTypeIdsMap.value)
}

const ensureCurrentUsage = (reward: LoyaltyReward) => {
  ensureRewardUsage(reward, props.usage)
}

const reloadReward = async (rewardId: number) => {
  if (props.usage === 'loyalty') {
    await loadLoyaltyProgramRewardById(rewardId)
  } else {
    await loadRewardById(rewardId)
  }

  if (currentReward.value) {
    ensureCurrentUsage(currentReward.value)
    hydrateItemsFromExternalId(currentReward.value)
  }

  initializeOfferFormIfNeeded()

  initialReward.value = JSON.parse(JSON.stringify(currentReward.value))

  await loadPartnerParameters()
}

watch(
  rewardVouchers,
  (next) => {
    const r = currentReward.value
    if (!r || r.id != null) return
    const slug = r.rewardType?.slug
    if (!slug) return
    const voucher = next.find((v) => v.slug === slug)
    if (!voucher) return
    if (r.rewardType?.id !== voucher.id) {
      r.rewardType.id = voucher.id
      r.rewardTypeId = voucher.id
    }
  },
  { flush: 'post' }
)

watch(
  () => currentReward.value?.rewardType,
  (_, oldVal) => {
    if (oldVal) {
      emit('update:rewardTypeChanged', currentReward.value)
    }
  }
)

onMounted(async () => {
  if (props.rewardId) {
    reloadReward(props.rewardId)
  } else {
    isLoading.value = true
    await fetchRewardTypes()
    if (!isRewardTypesReady.value) {
      emit('saveError', new Error(rewardTypesError.value || 'Unable to load reward types'))
      emit('close')
      return
    }
    const type = defaultRewardType.value
    if (!type) {
      emit('saveError', new Error('No reward types available'))
      emit('close')
      return
    }
    currentReward.value = {
      id: null,
      points: null,
      claimLimitQuantity: null,
      claimLimitDays: null,
      name: '',
      externalId: '',
      pictureUrl: '',
      value: null,
      validityInDays: null,
      minimumOrderAmount: null,
      voucherDiscountAmount: null,
      discountValuePercent: null,
      storeIds: [],
      rewardType: {
        name: type.text,
        id: type.id,
        slug: type.slug,
        isVoucher: type.isVoucher
      },
      rewardTypeId: type.id,
      notificationTextOverride: '',

      lists: [],
      segments: [],
      includedSegments: [],
      excludedSegments: [],
      items: [],
      stores: [],
      hasReachedClaimLimit: false,
      usages: [],

      autopilots: [],
      isForSingleUse: false
    }

    ensureCurrentUsage(currentReward.value)
    initializeOfferFormIfNeeded()
    isLoading.value = false
    await loadPartnerParameters()
  }
})
</script>

<template>
  <BaseModal
    :is-open="true"
    :heading-title="pageTitle"
    :is-processing="isLoading || isLoadingPartners"
    :cancel="t('global.words.cancel')"
    :link-text="t('global.words.validate')"
    :is-closable="false"
    size="full"
    :show-footer="false"
    content-class="bg-muted"
  >
    <template #customHeadingTitle>
      <div v-if="!isLoading && !isLoadingPartners" class="flex flex-row w-full">
        <div class="flex flex-1 flex-row items-center gap-3">
          <div class="flex items-center justify-center gap-2">
            <img
              :src="currentReward?.pictureUrl ? currentReward.pictureUrl : defaultUrl"
              alt="Reward picture"
              class="w-10 h-10 rounded-lg"
            />
          </div>
          <div class="flex flex-col items-start justify-center">
            <h3 class="text-base font-semibold text-foreground mb-0.5">{{ pageTitle }}</h3>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex gap-3">
            <Button variant="outline" @click="handleClose">
              {{ isAddMode ? t('global.actions.cancel') : t('global.actions.back') }}
            </Button>
            <Button
              variant="default-primary"
              :disabled="(!isAddMode && !hasChanges) || changesLoading"
              @click="handleSave"
            >
              <Loader2 v-if="changesLoading" class="animate-spin" />
              {{ isAddMode ? t('global.words.add') : t('global.actions.save') }}
            </Button>
          </div>
        </div>
      </div>
    </template>

    <div v-if="!isLoading && !isLoadingPartners" class="py-4 sm:py-6 px-2 sm:px-6">
      <div class="flex flex-col-reverse lg:flex-row gap-6 lg:gap-8 justify-center">
        <RewardForm
          ref="rewardFormRef"
          v-model:editing-reward="currentReward"
          :usage="usage"
          sideitem-top-offset="top-6"
          :hide-claim-limit-block="hideClaimLimitBlock"
          @update:nameChanged="emit('update:nameChanged', $event)"
        >
          <template #additional-form-blocks>
            <slot name="additional-form-blocks" />
          </template>

          <template #additional-summary>
            <slot name="additional-summary" :has-changes="hasChanges" />
          </template>
        </RewardForm>
      </div>
    </div>
  </BaseModal>
</template>
